import { ImmutableObject } from "@hookstate/core"
import { HighlightOff } from "@mui/icons-material"
import { useMediaQuery, useTheme } from "@mui/material"
import { FC } from "react"

import { uuid } from "uuidv4"

import { useBookings } from "../../../../states/bookings"
import { AdvancedForm, AdvancedForms } from "src/components/common/advancedForm"
import { Button } from "src/components/common/button"
import { Drawer } from "src/components/common/drawer"
import { IconButton } from "src/components/common/iconButton"
import { BasicLayoutStyleProps } from "src/components/layouts/basic"

import { create } from "src/helpers/bem"
import { addNotification } from "src/states/notifications"
import { addToCart, mergeCart, useCart } from "src/states/shop/carts"
import {
  mergeProductAttributes,
  mergeProductAttributesFormField,
  resetProductAttributes,
  resetProductAttributesForm,
  useProductAttributes,
} from "src/states/shop/productAttributes"
import { ProductVariant } from "src/states/shop/productVariants"

import { TranslationMessages } from "src/translations"

import styles from "./ShopDetailProductAttributesDialog.module.scss"

const bem = create(styles, "ShopDetailProductAttributesDialog")

export type ShopDetailProductAttributesDialogProps = {
  messages: TranslationMessages
  style: BasicLayoutStyleProps
}

export const ShopDetailProductAttributesDialog: FC<
  ShopDetailProductAttributesDialogProps
> = ({ messages, style }) => {
  const message = messages.pages.shop.detail.productFeatureDialog
  const cart = useCart()
  const { bookings } = useBookings()
  const { form, open, addNew, product, productVariant, cartProductId } =
    useProductAttributes()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))

  const currentBooking = bookings?.find((b) => b.id === cart.id)
  const isChargesCalculation =
    product?.productCalculation === "Charges" ||
    productVariant?.productCalculation === "Charges"

  const filteredForm = isChargesCalculation
    ? form.map((f) => ({
        ...f,
        fields: f.fields.filter((field) => field.field !== "Amount"),
      }))
    : form

  const handleAddToCart = async () => {
    const cartObject = {
      ...(addNew ? { id: uuid() } : { id: cartProductId }),
      productVariant: productVariant as ImmutableObject<ProductVariant>,
      productAttributeValues: form[0].fields
        .filter(
          (formField) =>
            formField.field === "Amount" ||
            (formField.conditions?.display !== false &&
              formField.type !== "content") ||
            (formField.conditions?.display === false &&
              formField.value !== undefined &&
              formField.value !== null &&
              formField.value !== ""),
        )
        .map((formField) => ({
          field: formField.field,
          value: (isChargesCalculation && formField.field === "Amount"
            ? currentBooking
              ? (
                  currentBooking.assignedStandSize * currentBooking.duration
                ).toString()
              : "0"
            : formField.value) as ImmutableObject<Record<string, unknown>>,
          referenceType:
            productVariant?.productAttributesForm?.fields?.find(
              (f) => f.field == formField.field,
            )?.referenceType ?? "ProductAttribute",
        })),
    }

    if (addNew) {
      addToCart(cartObject)
    } else {
      await mergeCart(cartObject)
    }

    resetProductAttributes()
    addNotification({
      autoclose: true,
      variant: "success",
      content: messages.pages.shop.cart.snackbar.add,
    })
  }

  const hasAmountField = form[0]?.fields?.some((pa) => pa.field === "Amount")

  const isProductInCart = cart.products.find(
    (cartProduct) => cartProduct.productVariant === productVariant,
  )

  const fieldsRequiredButNotFilled =
    filteredForm[0]?.fields?.filter(
      (f) => (!f.conditions || f.conditions.display) && f.required && !f.value,
    ) ?? []

  const fieldsWithErrors =
    filteredForm[0]?.fields?.filter(
      (f) => (!f.conditions || f.conditions.display) && f.errorText,
    ) ?? []

  return (
    <Drawer
      anchor="right"
      open={open}
      style={style}
      sx={{ zIndex: 1201 }}
      title={!cartProductId ? message.title : message.titleUpdate}
      PaperProps={{
        sx: {
          ...(isMobile && { width: "100%" }),
        },
      }}
      onClose={() => mergeProductAttributes({ open: false })}
      onOpen={() => mergeProductAttributes({ open: true })}
    >
      <div className={bem()}>
        <div className={bem("header")}>
          <h2 className={bem("title")}>
            {!cartProductId ? message.title : message.titleUpdate}
          </h2>
          <div className={bem("close")}>
            <IconButton
              color={"primary"}
              icon={HighlightOff}
              onClick={() => mergeProductAttributes({ open: false })}
            />
          </div>
        </div>
        <AdvancedForm
          data={filteredForm as AdvancedForms[]}
          handleMergeField={mergeProductAttributesFormField}
          handleReset={resetProductAttributesForm}
          id={cart.id}
          messages={messages}
        />
        <div className={bem("footer")}>
          <Button
            buttonType="secondary"
            title={cartProductId ? message.update : message.add}
            variant="outlined"
            disabled={
              fieldsRequiredButNotFilled.length > 0 ||
              fieldsWithErrors.length > 0 ||
              !hasAmountField
            }
            onClick={async () => handleAddToCart()}
          />
        </div>
      </div>
    </Drawer>
  )
}
